/**
 * Smart debouncing for SalaryLab calculator controllers.
 * Adjusts delay based on event type and suppresses calculations
 * when the primary input is obviously incomplete (below min digits).
 */

const DEFAULT_DELAYS = {
  typing: 300,
  deletion: 700,
  toggle: 150,
  submit: 0,
}

/**
 * Detect the semantic event type from a DOM event.
 * @param {Event} event
 * @returns {"typing"|"deletion"|"toggle"|"submit"}
 */
export function detectEventType(event) {
  if (!event) return "typing"

  if (event.type === "submit" || event.type === "click") return "submit"
  if (event.type === "change") return "toggle"

  if (event.inputType === "deleteContentBackward" ||
      event.inputType === "deleteContentForward" ||
      event.inputType === "deleteByCut") {
    return "deletion"
  }

  return "typing"
}

/**
 * Count significant digits in a value string (ignores commas, $, spaces, decimals).
 * @param {string} value
 * @returns {number}
 */
export function countDigits(value) {
  if (!value) return 0
  return value.replace(/[^0-9]/g, "").length
}

export class SmartDebounce {
  /**
   * @param {Function} callback - The function to call after debounce
   * @param {Object} options
   * @param {Object} [options.delays] - Delay overrides per event type
   * @param {number} [options.minDigits=0] - Minimum digits to allow scheduling (0 = no threshold)
   */
  constructor(callback, { delays, minDigits } = {}) {
    this._callback = callback
    this._delays = { ...DEFAULT_DELAYS, ...delays }
    this._minDigits = minDigits || 0
    this._timer = null
  }

  /**
   * Schedule a debounced callback.
   * @param {string} eventType - "typing", "deletion", "toggle", or "submit"
   * @param {string} [primaryInputValue] - Current value of the primary input for digit check
   */
  schedule(eventType, primaryInputValue) {
    this.cancel()

    const belowMinDigits = this._minDigits > 0 &&
      primaryInputValue !== undefined &&
      countDigits(primaryInputValue) < this._minDigits

    if (belowMinDigits && eventType !== "submit") return

    const delay = this._delays[eventType] ?? this._delays.typing

    if (delay === 0) {
      this._callback()
      return
    }

    this._timer = setTimeout(() => this._callback(), delay)
  }

  cancel() {
    if (this._timer) {
      clearTimeout(this._timer)
      this._timer = null
    }
  }
}
